// LAYOUT-SPEC §0 — one deliverable date (wireframe 1)
//
//   ONE QUESTION  Is this date owed, what was sent for it, and what did the
//                 government say?
//   PRIMARY OBJECT  One occurrence (Archetype C, detail side panel).
//   PRIMARY VERB  One button chosen by state: + Submit (Submit again once rejected),
//                 Record review while awaiting review, Mark held on a milestone.
//                 Everything else is in the All actions dropdown, in labelled
//                 sections: Submit / Record / Requests (waiver, extension) /
//                 Corrections (move, not owed, remove). Record review is the
//                 government's verdict and a separate grant
//                 (area:execution.verdict). A waiver or not-owed is dual
//                 control (D111): one person proposes, another approves. A
//                 waiver or extension also waits for the COR's answer
//                 between the two (SAD-866); not owed is internal.
//   WORST-CASE VOLUME  A handful of submissions, each with a few files and
//                 up to five reviews. Each is a bordered card: attempt, who and
//                 when, the latest status as a badge; files in an inset list
//                 (icon buttons preview or open); reviews last.
//
// The drawer stores only the occurrence id (the ?occ= param) and reads the
// row from GetOccurrence keyed on it. It never copies the row into state.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraDateField, SpecraEmptyState, SpecraFileRow, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraKeyValueRow, SpecraOverlayFooter, SpecraRecordGroups, SpecraSectionHeader, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronDown, ExternalLink, Eye, FileText, Link2 } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../../../contexts/authContext';
import { useArtifactNames, useUploadArtifacts } from '../../../../hooks/useDeliveryDocuments';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { DocumentPreviewDialog } from '../../_shared/components/DocumentPreviewDialog';
import { useProgramDocuments } from '../../Docs/hooks';
import { useContractCLINs } from '../../Programs/contractHooks';
import { useCmAccess } from '../access';
import { useCmDrawerParams } from '../drawerParams';
import { useApproveDecision, useDeliverableSettings, useDuty, useMoveOccurrence, useOccurrence, useProposeExtension, useProposeNotOwed, useProposeSubmissionEdit, useProposeSubmissionEvidence, useProposeWaiver, useProposeWaiverRevoke, useRejectDecision, useRemoveOccurrence, useUploadEvidenceFiles, useWorkstream } from '../hooks';
import { cmDate, cmDateLong, cmMonth, cmSpan, instantDay, todayISO } from '../lib/format';
import { describeRule } from '../lib/rule';
import { decisionStepLabel, HANDLING_LABEL, instantWaiver, NOT_OWED_COPY, NOT_OWED_REASON_LABEL, occurrenceStatus, ownerWaiver, PROGRAM_OWNER, tierLabel } from '../lib/status';
import { useCmLookups, useUserNames } from '../lookups';
import { DECISION_KIND_LABEL, DENY_REASON, decisionWhy, OPEN_STATES, REVOKE_HELP, revokeOutcome } from './AwaitingApproval';
import { CmDrawer } from './CmDrawer';
import { CommentThread } from './CommentThread';
import { CorAgreedShortcut, CorAnswerFields, corAnswerProblem, EMPTY_COR_ANSWER, useCorAnswerRunner } from './CorAnswer';
import { CmDecisionEvidence, CmEvidence, CmPendingSubmission, CmSourceButton, CmStatusBadge, DutyClinLabel, linkLabelFromUrl, occurrenceClinDisplay } from './common';
import { LogEventDrawer } from './LogEventDrawer';
const ROLL_NOTE = {
    following_federal_workday: 'a weekend or federal holiday rolls to the next workday',
    preceding_federal_workday: 'a weekend or federal holiday rolls to the workday before',
    none: 'no weekend or holiday roll'
};
const LINK_KIND_LABEL = {
    sharepoint_file: 'SharePoint',
    sharepoint_folder: 'SharePoint folder',
    sharepoint: 'SharePoint',
    powerbi: 'Power BI',
    servicenow: 'ServiceNow',
    confluence: 'Confluence'
};
function linkHost(url) {
    try {
        return new URL(url).hostname;
    } catch  {
        return 'Link';
    }
}
export function attachmentKindLabel(a) {
    if (!a.url) return 'File';
    return LINK_KIND_LABEL[a.linkKind] ?? linkHost(a.url);
}
const DECISION_WORD = {
    approved: 'Approved',
    rejected: 'Rejected',
    comments: 'Comments',
    auto_approved: 'Approved (review window lapsed)'
};
function isNotFound(err) {
    return err instanceof ConnectError && err.code === Code.NotFound;
}
function isDenied(err) {
    return err instanceof ConnectError && err.code === Code.PermissionDenied;
}
export function OccurrenceDrawer({ programId, occId }) {
    const params = useCmDrawerParams();
    const q = useOccurrence(programId, occId);
    const occ = q.data?.occurrence;
    const duty = useDuty(programId, occ?.dutyId);
    const team = useWorkstream(programId, occ?.workstreamId || undefined);
    const lk = useCmLookups(programId);
    const clinsQ = useContractCLINs(programId);
    const clins = clinsQ.data?.clins ?? [];
    const access = useCmAccess(programId);
    const names = useUserNames();
    const settings = useDeliverableSettings(programId);
    const { user } = useAuth();
    const [mode, setMode] = useState({
        kind: 'view'
    });
    const [preview, setPreview] = useState(null);
    const [historyOpen, setHistoryOpen] = useState([]);
    const [whyOpen, setWhyOpen] = useState([]);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [logging, setLogging] = useState(false);
    const close = ()=>params.close('occ');
    const remove = useRemoveOccurrence(programId, {
        success: 'Date removed',
        failure: 'Could not remove'
    });
    const submissions = useMemo(()=>[
            ...q.data?.submissions ?? []
        ].sort((a, b)=>b.attempt - a.attempt), [
        q.data?.submissions
    ]);
    const decisions = q.data?.decisions ?? [];
    const open = decisions.find((d)=>d.state === 'UnderReview');
    const latestLive = submissions.find((s)=>!s.withdrawn);
    // SAD-1314: evidence added to an applied waiver is its own decision; the
    // waiver lists the original document, then every approved addition, oldest
    // first. Decisions arrive newest first.
    const waiverDocs = useProgramDocuments(occ?.waiverDocumentId ? programId : null);
    const waiverDoc = occ?.waiverDocumentId ? waiverDocs.data?.items.find((x)=>x.id === occ.waiverDocumentId) : undefined;
    const { waiverAttachments, extensionAttachments, addedEvidence, evidencePending } = useMemo(()=>{
        const all = q.data?.decisions ?? [];
        // A removed waiver's files went with it: only what came after the latest removal.
        const removedAt = all.find((x)=>x.kind === 'waiver_revoke' && x.state === 'Applied')?.decidedAt;
        // Applied decisions of a kind, oldest first (they arrive newest first).
        const applied = (kind)=>[
                ...all
            ].reverse().filter((x)=>x.kind === kind && x.state === 'Applied' && (!removedAt || !kind.startsWith('waiver') || x.decidedAt > removedAt));
        const evidenceOf = (kind)=>applied(kind).flatMap((x)=>x.attachments ?? []);
        const pendingOf = (kind)=>all.some((x)=>x.kind === kind && OPEN_STATES.has(x.state));
        return {
            // The extra files sent with the waiver / extension itself.
            waiverAttachments: evidenceOf('waiver'),
            extensionAttachments: [
                ...evidenceOf('extension'),
                ...evidenceOf('extension_evidence')
            ],
            addedEvidence: evidenceOf('waiver_evidence'),
            evidencePending: {
                waiver: pendingOf('waiver_evidence'),
                extension: pendingOf('extension_evidence')
            }
        };
    }, [
        q.data?.decisions
    ]);
    const artifactNames = useArtifactNames(useMemo(()=>[
            ...submissions.flatMap((s)=>s.attachments.map((a)=>a.artifactId)),
            ...waiverAttachments.map((a)=>a.artifactId),
            ...extensionAttachments.map((a)=>a.artifactId),
            ...addedEvidence.map((a)=>a.artifactId)
        ].filter(Boolean), [
        submissions,
        waiverAttachments,
        extensionAttachments,
        addedEvidence
    ]));
    const toItem = (a, i)=>({
            key: `${a.artifactId || a.url}-${i}`,
            name: a.url ? a.label || a.url : artifactNames.get(a.artifactId) || a.label || 'File',
            url: a.url,
            artifactId: a.artifactId,
            linkKind: ''
        });
    const waiverItems = [
        ...occ?.waiverDocumentId ? [
            {
                key: `doc-${occ.waiverDocumentId}`,
                name: waiverDoc?.fileName || waiverDoc?.title || 'Evidence',
                url: '',
                artifactId: waiverDoc?.fileId ?? '',
                linkKind: ''
            }
        ] : [],
        ...[
            ...waiverAttachments,
            ...addedEvidence
        ].map(toItem)
    ];
    const extensionItems = extensionAttachments.map(toItem);
    const d = duty.data?.duty ?? (occ ? lk.dutyById.get(occ.dutyId) : undefined);
    const rules = [
        ...duty.data?.rules ?? [],
        ...(duty.data?.assignments ?? []).flatMap((a)=>a.teamRules)
    ];
    const rule = occ?.ruleId ? rules.find((r)=>r.id === occ.ruleId) : undefined;
    // The rule that took over from this date's rule: it starts the day after
    // that rule ended (a replacement, pangaea's supersededBy).
    const successor = rule?.effectiveTo ? rules.find((r)=>r.id !== rule.id && r.effectiveFrom === dayAfter(rule.effectiveTo)) : undefined;
    const status = occ ? occurrenceStatus(occ, d?.finalReviewTier) : undefined;
    if (q.error && isDenied(q.error)) {
        // A refusal is not a failure to load: retrying will not help, and the
        // date is not gone. Say so, and who can open it.
        return /*#__PURE__*/ _jsx(CmDrawer, {
            title: "Deliverable date",
            onClose: close,
            testId: Testids.cmOccurrence.drawer,
            children: /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.cmOccurrence.noAccess,
                children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    title: "You can't open this date",
                    description: "Your role on this program doesn't include this deliverable's dates. A Program Lead or Delivery Unit Manager on the program can open it, or give you access.",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: close,
                        children: "Close"
                    })
                })
            })
        });
    }
    if (q.error) {
        return /*#__PURE__*/ _jsx(CmDrawer, {
            title: "Deliverable date",
            onClose: close,
            testId: Testids.cmOccurrence.drawer,
            children: /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.cmOccurrence.notFound,
                children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    title: isNotFound(q.error) ? "This date isn't available" : 'Could not load this date',
                    description: isNotFound(q.error) ? 'It may have been retired by a schedule change, or the link points to a date on another program.' : apiErrorMessage(q.error, 'Try again in a moment.'),
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: close,
                        children: "Close"
                    })
                })
            })
        });
    }
    if (!occ || !status) {
        return /*#__PURE__*/ _jsx(CmDrawer, {
            title: "Deliverable date",
            onClose: close,
            testId: Testids.cmOccurrence.drawer,
            children: /*#__PURE__*/ _jsx(SpecraText, {
                tone: "muted",
                size: "caption",
                children: "Loading…"
            })
        });
    }
    const exercise = lk.exerciseById.get(occ.exerciseId);
    const ot = exercise ? lk.optionalTaskById.get(exercise.optionalTaskId) : undefined;
    const period = lk.periodById.get(occ.periodOfPerformanceId);
    const dateClin = occurrenceClinDisplay(d?.dutyKey ?? '', occ.periodOfPerformanceId, lk, clins);
    // The SOW's handling of this deliverable for the date's team: the team rule
    // that minted it, else the team's in-force SOW's assignment.
    const assignments = duty.data?.assignments ?? [];
    const teamSowIds = new Set((team.data?.sows ?? []).filter((x)=>x.state === 'in_force').map((x)=>x.id));
    const teamAssignment = assignments.find((a)=>a.teamRules.some((r)=>r.id === occ.ruleId)) ?? assignments.find((a)=>teamSowIds.has(a.sowId));
    const owedUnder = exercise ? {
        main: `${ot?.short ? `${ot.short} ` : ''}exercise ${exercise.clinNumber || ''}`.trim(),
        span: cmSpan(exercise.start, exercise.end)
    } : period ? {
        main: period.name,
        span: cmSpan(period.start, period.end)
    } : {
        main: occ.ruleId ? 'The contract period this rule covers' : `Added by hand${occ.createdByUserId ? ` by ${names(occ.createdByUserId)}` : ''}`
    };
    const roll = settings.data?.roll ? ROLL_NOTE[settings.data.roll] : undefined;
    const closedState = [
        'not_owed',
        'retired'
    ].includes(status.state);
    const waivedOrClosed = closedState || status.state === 'waived';
    const waived = !!(occ.waivedOn || occ.waiverReason);
    const isMine = (userId)=>!!user?.id && userId === user.id;
    // A date added by hand is removed; a generated one is proposed not owed,
    // since the contract minted it. Neither while it has a live filing or an
    // open decision (the server refuses both).
    const addedByHand = !occ.ruleId;
    // SAD-834: a date that dates an event (the kickoff) is what every rule
    // anchored on the event projects from. It moves only by an extension a
    // second person approves; waiving or not-owing it leaves its dependents
    // waiting with no date.
    // SAD-835: so is a date other deliverables follow by its own submission
    // or acceptance (occ.dependents counts both kinds of follower).
    const eventDate = !!occ.event && !occ.event.projected || !occ.event?.projected && occ.dependents > 0;
    const dependents = eventDate ? Math.max(occ.dependents, occ.event?.dependents ?? 0) : 0;
    // SAD-837 / SAD-869: a date of a milestone (a duty that dates an event, the
    // kickoff meeting) is met by marking it held, never by a filing or a
    // review: its verb is Mark held, not Submit.
    const datesEvent = d?.eventKind ?? '';
    const eventName = occ.event?.name || d?.title || 'the event';
    const title = d?.title || 'Deliverable';
    const periodLabel = occ.label || cmMonth(occ.nominalDueOn || occ.dueOn);
    // One primary verb by state; everything else lives in All actions. Hidden
    // entirely for a read-only viewer; a verdict-only viewer sees only Record review.
    const canFile = access.canWrite && !closedState;
    // No primary verb once a date is waived or approved; the menu is unchanged.
    const canPrimary = canFile && ![
        'waived',
        'approved'
    ].includes(status.state);
    const primary = canPrimary && datesEvent ? occ.event?.heldOn ? null : /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        onClick: ()=>setLogging(true),
        "data-testid": Testids.cmOccurrence.logEvent,
        children: "Mark held"
    }) : status.state === 'awaiting_review' ? access.canVerdict && latestLive ? /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        onClick: ()=>params.open('review', latestLive.id),
        "data-testid": Testids.cmOccurrence.recordReview,
        children: "Record review"
    }) : null : canPrimary && (!latestLive || status.state === 'rejected') ? /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        onClick: ()=>params.open('submit', occ.id),
        "data-testid": Testids.cmOccurrence.submit,
        children: latestLive ? 'Submit' : '+ Submit'
    }) : null;
    const menuSections = [
        {
            label: 'Submit',
            items: canFile && !datesEvent ? [
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    onSelect: ()=>params.open('submit', occ.id),
                    "data-testid": Testids.cmOccurrence.submitItem,
                    children: "Submit evidence"
                }, "submit")
            ] : []
        },
        {
            label: 'Record',
            items: access.canVerdict && latestLive ? [
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    onSelect: ()=>params.open('review', latestLive.id),
                    "data-testid": Testids.cmOccurrence.recordReviewItem,
                    children: "Record review"
                }, "review")
            ] : []
        },
        {
            label: 'Requests',
            items: !canFile ? [] : [
                !open && !waivedOrClosed ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    onSelect: ()=>setMode({
                            kind: 'waiver'
                        }),
                    "data-testid": Testids.cmOccurrence.proposeWaiver,
                    children: "Propose waiver"
                }, "waiver") : null,
                // A wrongly waived date is fixed by removing the waiver: it is owed
                // again, and may then be marked not owed.
                !open && waived && !closedState ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    onSelect: ()=>setMode({
                            kind: 'revokeWaiver'
                        }),
                    "data-testid": Testids.cmOccurrence.revokeWaiver,
                    children: "Remove waiver"
                }, "revokeWaiver") : null,
                // SAD-866: any date's extension is proposed to the COR; a date that
                // dates an event moves no other way.
                open ? null : /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    onSelect: ()=>setMode({
                            kind: 'extension'
                        }),
                    "data-testid": Testids.cmOccurrence.proposeExtension,
                    children: "Propose extension"
                }, "extension")
            ]
        },
        {
            label: 'Corrections',
            items: !canFile ? [] : [
                eventDate ? null : /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    onSelect: ()=>setMode({
                            kind: 'move'
                        }),
                    "data-testid": Testids.cmOccurrence.move,
                    children: "Move due date"
                }, "move"),
                !open && !waivedOrClosed && !addedByHand ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    onSelect: ()=>setMode({
                            kind: 'notOwed'
                        }),
                    "data-testid": Testids.cmOccurrence.proposeNotOwed,
                    children: NOT_OWED_COPY.menu
                }, "notOwed") : null,
                addedByHand && !open && !latestLive ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    onSelect: ()=>setConfirmRemove(true),
                    "data-testid": Testids.cmOccurrence.remove,
                    children: "Remove date"
                }, "remove") : null
            ]
        }
    ].map((sec)=>({
            ...sec,
            items: sec.items.filter(Boolean)
        })).filter((sec)=>sec.items.length > 0);
    const headerActions = mode.kind === 'view' && (access.canWrite || access.canVerdict) ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            primary,
            menuSections.length > 0 ? /*#__PURE__*/ _jsxs(DropdownMenu, {
                children: [
                    /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsxs(SpecraButton, {
                            size: "sm",
                            variant: "secondary",
                            "data-testid": Testids.cmOccurrence.more,
                            children: [
                                "All actions",
                                /*#__PURE__*/ _jsx(ChevronDown, {
                                    className: "h-4 w-4",
                                    "aria-hidden": true
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(DropdownMenuContent, {
                        align: "end",
                        children: menuSections.map((sec, i)=>/*#__PURE__*/ _jsxs(Fragment, {
                                children: [
                                    i > 0 ? /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}) : null,
                                    /*#__PURE__*/ _jsxs(DropdownMenuGroup, {
                                        children: [
                                            /*#__PURE__*/ _jsx(DropdownMenuLabel, {
                                                className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                                                children: sec.label
                                            }),
                                            sec.items
                                        ]
                                    })
                                ]
                            }, sec.label))
                    })
                ]
            }) : null
        ]
    }) : undefined;
    const origin = `${d?.origin === 'sow' ? 'SOW deliverable' : d?.origin === 'manual' ? 'Added by hand' : 'Award'}${d?.pwsRef ? ` PWS ${d.pwsRef}` : ''}${d?.letter ? ` deliverable ${d.letter}` : ''}${d?.sourceLocator ? ` · ${d.sourceLocator}` : ''}`;
    return /*#__PURE__*/ _jsxs(CmDrawer, {
        title: /*#__PURE__*/ _jsx("span", {
            "data-testid": Testids.cmOccurrence.title,
            children: title
        }),
        description: /*#__PURE__*/ _jsxs("span", {
            className: "block space-y-2",
            children: [
                /*#__PURE__*/ _jsxs("span", {
                    className: "flex flex-wrap items-center gap-x-2",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            children: periodLabel
                        }),
                        dateClin.label ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    "aria-hidden": true,
                                    children: "·"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    "data-testid": Testids.cmOccurrence.clin,
                                    children: /*#__PURE__*/ _jsx(DutyClinLabel, {
                                        display: dateClin,
                                        tone: "primary"
                                    })
                                })
                            ]
                        }) : null,
                        /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": true,
                            children: "·"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: lk.teamName(occ.workstreamId)
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("span", {
                    className: "flex flex-wrap items-center gap-x-2",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "text-[length:var(--fs-ui-body)] font-medium text-foreground",
                            "data-testid": Testids.cmOccurrence.due,
                            children: cmDateLong(occ.dueOn, 'date not stated')
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": true,
                            children: "·"
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "inline-flex flex-wrap items-center gap-1",
                            children: [
                                /*#__PURE__*/ _jsx(CmStatusBadge, {
                                    status: status,
                                    testId: Testids.cmOccurrence.status
                                }),
                                /*#__PURE__*/ _jsx(CmPendingSubmission, {
                                    occ: occ
                                })
                            ]
                        })
                    ]
                })
            ]
        }),
        eyebrow: /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex flex-wrap items-center gap-2",
            children: [
                origin,
                /*#__PURE__*/ _jsx(CmSourceButton, {
                    locator: d?.sourceLocator
                })
            ]
        }),
        headerActions: headerActions,
        onClose: close,
        testId: Testids.cmOccurrence.drawer,
        footer: mode.kind === 'waiver' || mode.kind === 'notOwed' ? /*#__PURE__*/ _jsx(ProposeForm, {
            programId: programId,
            occurrenceId: occ.id,
            kind: mode.kind,
            eventName: eventDate ? occ.event?.name || title : undefined,
            dependents: dependents,
            onExtend: ()=>setMode({
                    kind: 'extension'
                }),
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : mode.kind === 'extension' ? /*#__PURE__*/ _jsx(ExtensionForm, {
            programId: programId,
            occurrenceId: occ.id,
            currentDue: occ.dueOn,
            dependents: dependents,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : mode.kind === 'revokeWaiver' ? /*#__PURE__*/ _jsx(RevokeWaiverForm, {
            programId: programId,
            occurrenceId: occ.id,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : mode.kind === 'move' ? /*#__PURE__*/ _jsx(MoveForm, {
            programId: programId,
            occurrenceId: occ.id,
            currentDue: occ.dueOn,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : mode.kind === 'cor' ? /*#__PURE__*/ _jsx(CorForm, {
            programId: programId,
            decision: mode.decision,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : mode.kind === 'end' ? /*#__PURE__*/ _jsx(EndDecisionForm, {
            programId: programId,
            decision: mode.decision,
            withdraw: mode.withdraw,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : mode.kind === 'editSubmission' ? /*#__PURE__*/ _jsx(EditSubmissionForm, {
            programId: programId,
            occurrenceId: occ.id,
            submission: mode.submission,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : mode.kind === 'addEvidence' ? /*#__PURE__*/ _jsx(AddEvidenceForm, {
            programId: programId,
            occurrenceId: occ.id,
            submission: mode.submission,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : null,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-8",
                children: [
                    occ.waivedOn || occ.waiverReason ? /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-1 rounded-md border border-border p-3",
                        "data-testid": Testids.cmOccurrence.waiverNote,
                        children: [
                            instantWaiver(occ.waiverReason) || occ.waiverAuthority === PROGRAM_OWNER ? /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "body-sm",
                                className: "whitespace-pre-line text-left",
                                children: waiverLine(occ, decisions, names)
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                        variant: "group",
                                        title: "Waived by the COR"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                        label: "Date waived",
                                        value: occ.waivedOn ? cmDate(occ.waivedOn) : 'Not recorded'
                                    }),
                                    occ.waiverReason ? /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-0.5 py-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraText, {
                                                as: "p",
                                                size: "caption",
                                                tone: "muted",
                                                children: "Reason"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraText, {
                                                as: "p",
                                                size: "body-sm",
                                                className: "whitespace-pre-wrap text-left",
                                                children: occ.waiverReason
                                            })
                                        ]
                                    }) : null
                                ]
                            }),
                            waiverItems.length > 0 ? /*#__PURE__*/ _jsx(AttachmentList, {
                                items: waiverItems,
                                onPreview: setPreview,
                                testId: Testids.cmOccurrence.evidence
                            }) : null,
                            evidencePending.waiver ? /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "caption",
                                tone: "muted",
                                children: "Evidence pending approval"
                            }) : null
                        ]
                    }) : null,
                    open ? /*#__PURE__*/ _jsx(PendingDecisionBanner, {
                        decision: open,
                        proposer: names(open.proposedByUserId),
                        mine: isMine(open.proposedByUserId),
                        canAct: access.canWrite && mode.kind === 'view',
                        programId: programId,
                        onEnd: (withdraw)=>setMode({
                                kind: 'end',
                                decision: open,
                                withdraw
                            }),
                        onRecordCor: ()=>setMode({
                                kind: 'cor',
                                decision: open
                            })
                    }) : null,
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: datesEvent ? 'Held' : 'Submissions'
                            }),
                            submissions.length === 0 && datesEvent ? /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        size: "caption",
                                        tone: "muted",
                                        children: occ.event?.heldOn ? `The ${eventName} was held on ${cmDate(occ.event.heldOn)}.` : `A milestone, not a deliverable: mark the ${eventName} held when it happens. It needs no file and no review, and it never counts as late.`
                                    }),
                                    occ.event?.heldDocumentId ? /*#__PURE__*/ _jsx(CmEvidence, {
                                        programId: programId,
                                        documentId: occ.event.heldDocumentId,
                                        testId: Testids.cmOccurrence.evidence
                                    }) : null
                                ]
                            }) : submissions.length === 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "caption",
                                tone: "muted",
                                children: [
                                    "Nothing submitted for this date yet.",
                                    canPrimary && !datesEvent ? ' Use + Submit when it goes out.' : ''
                                ]
                            }) : /*#__PURE__*/ _jsx("ol", {
                                className: "space-y-3",
                                children: submissions.map((s)=>/*#__PURE__*/ _jsx(SubmissionItem, {
                                        s: s,
                                        latest: s.id === latestLive?.id,
                                        statusLabel: s.id === latestLive?.id ? status.label : undefined,
                                        statusTone: status.tone,
                                        artifactNames: artifactNames,
                                        onPreview: setPreview,
                                        canEdit: access.canWrite && !closedState && !s.withdrawn && !open && mode.kind === 'view',
                                        onEdit: ()=>setMode({
                                                kind: 'editSubmission',
                                                submission: s
                                            }),
                                        onAddEvidence: ()=>setMode({
                                                kind: 'addEvidence',
                                                submission: s
                                            })
                                    }, s.id))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraRecordGroups, {
                        asDetails: true,
                        variant: "hairline",
                        openIds: whyOpen,
                        onOpenChange: setWhyOpen,
                        getGroupTestId: ()=>Testids.cmOccurrence.whyDue,
                        groups: [
                            {
                                id: 'why-due',
                                header: {
                                    title: 'Why this date is due'
                                },
                                content: /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                            label: "Rule",
                                            value: rule ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    describeRule(rule),
                                                    roll ? `; ${roll}` : ''
                                                ]
                                            }) : 'Added by hand, not scheduled by a rule'
                                        }),
                                        occ.movedFromDueOn ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                            label: "Moved",
                                            value: /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    "Moved from ",
                                                    cmDate(occ.movedFromDueOn),
                                                    ": ",
                                                    occ.movedReason,
                                                    occ.movedDocumentId ? /*#__PURE__*/ _jsx("span", {
                                                        className: "block",
                                                        children: /*#__PURE__*/ _jsx(CmEvidence, {
                                                            programId: programId,
                                                            documentId: occ.movedDocumentId,
                                                            testId: Testids.cmOccurrence.evidence
                                                        })
                                                    }) : null,
                                                    extensionItems.length > 0 ? /*#__PURE__*/ _jsx(AttachmentList, {
                                                        items: extensionItems,
                                                        onPreview: setPreview,
                                                        testId: Testids.cmOccurrence.evidence
                                                    }) : null,
                                                    evidencePending.extension ? /*#__PURE__*/ _jsx("span", {
                                                        className: "block text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                                        children: "Evidence pending approval"
                                                    }) : null
                                                ]
                                            })
                                        }) : null,
                                        /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                            "data-testid": Testids.cmOccurrence.owedBecause,
                                            label: "Owed under",
                                            value: /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    owedUnder.main,
                                                    owedUnder.span ? /*#__PURE__*/ _jsxs("span", {
                                                        className: "block text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                                        children: [
                                                            "PoP ",
                                                            owedUnder.span
                                                        ]
                                                    }) : null
                                                ]
                                            })
                                        }),
                                        occ.coveredByDutyId ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                            "data-testid": Testids.cmOccurrence.coveredNote,
                                            label: "Covered",
                                            value: coveredLine(lk.dutyById.get(occ.coveredByDutyId))
                                        }) : null,
                                        /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                            "data-testid": Testids.cmOccurrence.responsibility,
                                            label: "Responsibility",
                                            value: /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    lk.teamName(occ.workstreamId),
                                                    teamAssignment ? /*#__PURE__*/ _jsxs("span", {
                                                        className: "block text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                                        children: [
                                                            HANDLING_LABEL[teamAssignment.handling] ?? teamAssignment.handling,
                                                            ' ',
                                                            "here",
                                                            teamAssignment.teamRules.some((r)=>r.id === occ.ruleId) ? ' (team rule)' : ''
                                                        ]
                                                    }) : null
                                                ]
                                            })
                                        })
                                    ]
                                })
                            }
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CommentThread, {
                        programId: programId,
                        target: {
                            type: 'occurrence',
                            id: occ.id
                        }
                    }),
                    /*#__PURE__*/ _jsx(SpecraRecordGroups, {
                        asDetails: true,
                        variant: "hairline",
                        openIds: historyOpen,
                        onOpenChange: setHistoryOpen,
                        getGroupTestId: ()=>Testids.cmOccurrence.history,
                        groups: [
                            {
                                id: 'history',
                                header: {
                                    title: 'History'
                                },
                                content: /*#__PURE__*/ _jsx("ul", {
                                    className: "space-y-1 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                    children: historyLines(occ, decisions, names, rule ? describeRule(rule) : '', successor ? `by the rule “${successor.ruleText || describeRule(successor)}” from ${cmDate(successor.effectiveFrom)}` : '').map((line, i)=>// Two history lines can read the same (a waiver proposed twice).
                                        /*#__PURE__*/ _jsx("li", {
                                            "data-testid": Testids.cmOccurrence.historyRow,
                                            children: line
                                        }, `${i}-${line}`))
                                })
                            }
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: preview?.artifactId ?? '',
                title: preview?.name ?? 'Submission file',
                open: !!preview,
                onOpenChange: (o)=>{
                    if (!o) setPreview(null);
                }
            }),
            logging ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                kind: datesEvent,
                milestone: true,
                workstreamId: occ.workstreamId,
                onClose: ()=>setLogging(false)
            }) : null,
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: confirmRemove,
                onOpenChange: setConfirmRemove,
                title: "Remove this date?",
                description: "It was added by hand, so it is removed outright rather than marked not owed.",
                isPending: remove.isPending,
                onConfirm: ()=>remove.mutate({
                        occurrenceId: occ.id
                    }, {
                        onSuccess: close
                    })
            })
        ]
    });
}
/** R22-A: the deliverable whose own series delivers a covered date. */ export function coveredLine(by) {
    if (!by) return 'Covered by another deliverable';
    return `Covered by ${[
        by.dutyKey,
        by.title
    ].filter(Boolean).join(' ')}`;
}
/**
 * Who waived a date and when (R22-A). An instant approver's waiver (the
 * reason ends " · instant: <role>") is applied by that person, with no COR
 * step: their name, role and day from the applied waiver. A waiver the
 * program owner decided names the owner's decision and who approved it. Any
 * other waiver is the COR's, on the COR's day.
 */ export function waiverLine(occ, decisions, names) {
    const role = instantWaiver(occ.waiverReason);
    if (occ.waiverAuthority === PROGRAM_OWNER) {
        const applied = decisions.find((d)=>d.kind === 'waiver' && d.state === 'Applied');
        const who = applied?.decidedByUserId || applied?.approvedByUserId;
        const on = applied?.decidedAt ? ` on ${cmDate(instantDay(applied.decidedAt))}` : '';
        return `Waived by program owner decision${who ? `, approved by ${names(who)}` : ''}${on}: “${occ.waiverReason}”`;
    }
    if (!role) {
        return `By the COR on ${occ.waivedOn ? cmDate(occ.waivedOn) : 'a date not recorded'}${occ.waiverReason ? `: “${occ.waiverReason}”` : ''}`;
    }
    const note = occ.waiverReason.replace(/\s·\s*instant:.*$/, '').trim();
    const applied = decisions.find((d)=>d.kind === 'waiver' && d.state === 'Applied');
    const who = applied?.decidedByUserId || applied?.approvedByUserId;
    const by = who ? ` by ${names(who)} (${role})` : ` (${role})`;
    const on = applied?.decidedAt ? ` on ${cmDate(instantDay(applied.decidedAt))}` : '';
    return `Waived instantly${by}${on}${note ? `: “${note}”` : ''}`;
}
/** The day after an ISO date (YYYY-MM-DD). */ function dayAfter(iso) {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
}
export function historyLines(occ, decisions, names, ruleWords, supersededBy = '') {
    const out = [];
    out.push(occ.ruleId ? `Scheduled by the rule${ruleWords ? `: ${ruleWords}` : ''}` : `Added by hand${occ.createdByUserId ? ` by ${names(occ.createdByUserId)}` : ''}`);
    if (occ.movedFromDueOn) out.push(`Due date moved from ${cmDate(occ.movedFromDueOn)} to ${cmDate(occ.dueOn)}: ${occ.movedReason}`);
    for (const d of [
        ...decisions
    ].reverse()){
        const what = d.kind === 'not_owed' ? 'Not-owed proposal' : DECISION_KIND_LABEL[d.kind] ?? 'Proposal';
        // SAD-866: the proposer's note, then the COR's answer on its own line.
        const why = d.kind === 'not_owed' || d.kind === 'submission_edit' || d.kind === 'submission_evidence' || d.kind === 'waiver_evidence' || d.kind === 'extension_evidence' || d.kind === 'waiver_edit' || d.kind === 'waiver_revoke' ? decisionWhy(d) : d.note;
        const proposed = d.kind === 'waiver' ? 'Waiver proposed' : d.kind === 'extension' ? `Extension to ${cmDate(d.dueOn)} proposed` : d.kind === 'submission_edit' ? 'Submission edit proposed' : d.kind === 'submission_evidence' || d.kind === 'waiver_evidence' || d.kind === 'extension_evidence' ? 'Evidence addition proposed' : d.kind === 'waiver_edit' ? 'Waiver edit proposed' : d.kind === 'waiver_revoke' ? 'Waiver removal proposed' : 'Proposed as not owed';
        const via = d.viaAssist ? ' via Specra Assist' : '';
        out.push(`${proposed} by ${names(d.proposedByUserId)} on ${cmDate(instantDay(d.proposedAt))}${via}${why ? `: “${why}”` : ''}`);
        if (d.kind !== 'not_owed' && d.kind !== 'submission_edit' && d.kind !== 'submission_evidence' && d.kind !== 'waiver_evidence' && d.kind !== 'extension_evidence' && d.kind !== 'waiver_edit' && d.kind !== 'waiver_revoke' && d.reason) out.push(ownerWaiver(d) ? `The program owner decided, no COR step: “${d.reason}”` : instantWaiver(d.reason) ? `No COR step: applied instantly (${instantWaiver(d.reason)})` : `The COR agreed${d.corAgreedOn ? ` on ${cmDate(d.corAgreedOn)}` : ''}: “${d.reason}”`);
        const on = d.decidedAt ? ` on ${cmDate(instantDay(d.decidedAt))}` : '';
        if (d.state === 'Applied') out.push(`${what} approved by ${names(d.decidedByUserId || d.approvedByUserId)}${on}`);
        if (d.step === 'cor_declined') out.push(`The COR declined (recorded by ${names(d.decidedByUserId)}${on}): “${d.stateReason}”`);
        else if (d.state === 'Rejected') out.push(`${what} rejected by ${names(d.decidedByUserId)}${on}: “${d.stateReason}”`);
        if (d.state === 'Cancelled') out.push(`${what} withdrawn by ${names(d.decidedByUserId)}${on}: “${d.stateReason}”`);
    }
    if (occ.retired) out.push(`Retired: ${occ.retiredReason.replace(/_/g, ' ')}${occ.retiredReason === 'superseded' && supersededBy ? ` ${supersededBy}` : ''}`);
    return out;
}
function PendingDecisionBanner({ decision, proposer, mine, canAct, programId, onEnd, onRecordCor }) {
    const approve = useApproveDecision(programId, {
        success: 'Approved'
    });
    const deny = useRejectDecision(programId, {
        success: 'Proposal denied'
    });
    const deciding = approve.isPending || deny.isPending;
    // SAD-866: a waiver or extension waits for the COR before anyone approves.
    // SAD-902: submission edits need no COR.
    const awaitingCor = decision.step === 'proposed';
    const submissionChange = decision.kind === 'submission_edit' || decision.kind === 'submission_evidence' || decision.kind === 'waiver_evidence' || decision.kind === 'extension_evidence' || decision.kind === 'waiver_edit' || decision.kind === 'waiver_revoke';
    const what = decision.kind === 'extension' ? `Extension to ${cmDate(decision.dueOn)}` : decision.kind === 'submission_edit' && decision.submittedOn ? `Edit to submitted ${cmDate(decision.submittedOn)}` : DECISION_KIND_LABEL[decision.kind] ?? 'Proposal';
    const note = decision.kind === 'not_owed' || submissionChange ? decisionWhy(decision) : decision.note;
    // SAD-917: submission edit / evidence denials ask for a short reason
    // (EndDecisionForm), matching the reject form used for other proposals.
    const withdrawOrDeny = mine ? /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        variant: "secondary",
        onClick: ()=>onEnd(true),
        "data-testid": Testids.cmOccurrence.withdrawDecision,
        children: "Withdraw"
    }) : submissionChange ? /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        variant: "secondary",
        disabled: deciding,
        onClick: ()=>onEnd(false),
        "data-testid": Testids.cmOccurrence.rejectDecision,
        children: "Deny"
    }) : /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        variant: "secondary",
        isLoading: deny.isPending,
        disabled: deciding,
        onClick: ()=>deny.mutate({
                changeSetId: decision.changeSetId,
                reason: DENY_REASON
            }),
        "data-testid": Testids.cmOccurrence.rejectDecision,
        children: "Deny"
    });
    const actions = !canAct ? undefined : awaitingCor ? /*#__PURE__*/ _jsxs("div", {
        className: "flex gap-2",
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                onClick: onRecordCor,
                "data-testid": Testids.cmOccurrence.recordCor,
                children: "Record the COR’s answer"
            }),
            withdrawOrDeny
        ]
    }) : mine ? withdrawOrDeny : /*#__PURE__*/ _jsxs("div", {
        className: "flex gap-2",
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                isLoading: approve.isPending,
                disabled: deciding,
                onClick: ()=>approve.mutate({
                        changeSetId: decision.changeSetId
                    }),
                "data-testid": Testids.cmOccurrence.approveDecision,
                children: "Approve"
            }),
            withdrawOrDeny
        ]
    });
    return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "warning",
        variant: "framed",
        title: decisionStepLabel(decision) || 'Awaiting approval',
        "data-testid": Testids.cmOccurrence.pendingDecision,
        "data-step": decision.step,
        action: actions,
        children: [
            what,
            " proposed by ",
            proposer,
            " on ",
            cmDate(instantDay(decision.proposedAt)),
            note ? `: “${note}”` : '',
            ".",
            ' ',
            !submissionChange && decision.kind !== 'not_owed' && decision.reason ? ownerWaiver(decision) ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "The program owner decided: “",
                    decision.reason,
                    "”. "
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "The COR agreed",
                    decision.corAgreedOn ? ` on ${cmDate(decision.corAgreedOn)}` : '',
                    ": “",
                    decision.reason,
                    "”.",
                    ' '
                ]
            }) : null,
            awaitingCor ? 'The date is still owed: nothing changes until the COR agrees and a second person approves.' : decision.kind === 'waiver_revoke' ? mine ? 'The waiver stands until someone else approves; you can withdraw this removal.' : 'The waiver stands until a second person approves its removal.' : submissionChange ? mine ? 'The original filing stands until someone else approves; you can withdraw this edit.' : 'The original filing stands until a second person approves this edit.' : mine ? 'Someone else must approve it; you can withdraw it.' : 'A second person must approve it before it takes effect.',
            decision.evidenceDocumentId || decision.attachments.length > 0 ? /*#__PURE__*/ _jsx("span", {
                className: "mt-1 block",
                children: /*#__PURE__*/ _jsx(CmDecisionEvidence, {
                    programId: programId,
                    evidenceDocumentId: decision.evidenceDocumentId,
                    attachments: decision.attachments,
                    testId: Testids.cmOccurrence.evidence
                })
            }) : null
        ]
    });
}
/** The inset file list under a submission or a waiver: files preview, links open. */ function AttachmentList({ items, onPreview, testId }) {
    return /*#__PURE__*/ _jsx("ul", {
        className: "divide-y divide-border rounded-md bg-muted/40 px-2",
        "data-testid": testId,
        children: items.map((a)=>{
            const preview = ()=>onPreview({
                    artifactId: a.artifactId,
                    name: a.name
                });
            return /*#__PURE__*/ _jsx("li", {
                children: /*#__PURE__*/ _jsx(SpecraFileRow, {
                    className: "py-1.5",
                    "data-testid": Testids.cmOccurrence.attachment,
                    icon: a.url ? /*#__PURE__*/ _jsx(Link2, {
                        className: "h-3.5 w-3.5",
                        "aria-hidden": true
                    }) : /*#__PURE__*/ _jsx(FileText, {
                        className: "h-3.5 w-3.5",
                        "aria-hidden": true
                    }),
                    name: a.url || !a.artifactId ? a.name : /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "link",
                        size: "inline",
                        className: "max-w-full truncate text-foreground",
                        onClick: preview,
                        children: a.name
                    }),
                    meta: attachmentKindLabel(a),
                    actions: !a.url && !a.artifactId ? undefined : a.url ? /*#__PURE__*/ _jsx(SpecraButton, {
                        asChild: true,
                        size: "sm",
                        variant: "ghost",
                        "aria-label": "Open link",
                        title: "Open link",
                        children: /*#__PURE__*/ _jsx("a", {
                            href: a.url,
                            target: "_blank",
                            rel: "noreferrer noopener",
                            children: /*#__PURE__*/ _jsx(ExternalLink, {
                                "aria-hidden": true
                            })
                        })
                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "aria-label": "Preview file",
                        title: "Preview file",
                        onClick: preview,
                        children: /*#__PURE__*/ _jsx(Eye, {
                            "aria-hidden": true
                        })
                    })
                })
            }, a.key);
        })
    });
}
export function SubmissionItem({ s, latest, statusLabel, statusTone, artifactNames, onPreview, canEdit, onEdit, onAddEvidence }) {
    return /*#__PURE__*/ _jsxs("li", {
        className: `space-y-2 rounded-md border border-border p-3${s.withdrawn ? ' opacity-60' : ''}`,
        "data-testid": Testids.cmOccurrence.submissionRow,
        "data-row-id": s.id,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-x-2 gap-y-1 text-[length:var(--fs-ui-body)]",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "font-medium",
                        "data-testid": Testids.cmOccurrence.attempt,
                        children: [
                            "#",
                            s.attempt
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            "Submitted ",
                            cmDate(s.submittedOn, 'on an unstated day'),
                            s.submittedByDisplay ? ` by ${s.submittedByDisplay}` : ''
                        ]
                    }),
                    s.withdrawn ? /*#__PURE__*/ _jsxs("span", {
                        className: "text-muted-foreground",
                        children: [
                            "Withdrawn: ",
                            s.withdrawnReason
                        ]
                    }) : null,
                    s.attestedBy ? /*#__PURE__*/ _jsx("span", {
                        className: "text-muted-foreground",
                        title: s.attestedBy,
                        "data-testid": Testids.cmOccurrence.attested,
                        children: "Attested by evidence, no document"
                    }) : null,
                    /*#__PURE__*/ _jsxs("span", {
                        className: "ml-auto flex items-center gap-1",
                        children: [
                            canEdit ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: onEdit,
                                        "data-testid": Testids.cmOccurrence.editSubmission,
                                        children: "Edit"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: onAddEvidence,
                                        "data-testid": Testids.cmOccurrence.addEvidence,
                                        children: "Add evidence"
                                    })
                                ]
                            }) : null,
                            latest && statusLabel ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: statusTone ?? 'neutral',
                                children: statusLabel
                            }) : null
                        ]
                    })
                ]
            }),
            s.attachments.length > 0 ? /*#__PURE__*/ _jsx(AttachmentList, {
                items: s.attachments.map((a)=>({
                        key: a.id,
                        url: a.url,
                        artifactId: a.artifactId,
                        linkKind: a.linkKind,
                        name: a.url ? a.label || a.url : artifactNames.get(a.artifactId) || a.label || 'File'
                    })),
                onPreview: onPreview
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        children: "Reviews"
                    }),
                    s.reviews.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        children: "None yet"
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1 text-[length:var(--fs-ui-caption)]",
                        children: s.reviews.map((r)=>/*#__PURE__*/ _jsxs("li", {
                                className: r.superseded ? 'text-muted-foreground line-through' : '',
                                "data-testid": Testids.cmOccurrence.reviewRow,
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: r.superseded ? 'neutral' : r.decision === 'rejected' ? 'danger' : r.decision === 'approved' || r.decision === 'auto_approved' ? 'success' : 'neutral',
                                        children: DECISION_WORD[r.decision] ?? r.decision
                                    }),
                                    ' ',
                                    r.reviewerDisplay || 'Reviewer not named',
                                    " · ",
                                    tierLabel(r.tier),
                                    " ·",
                                    ' ',
                                    cmDate(r.decidedOn),
                                    r.notes ? /*#__PURE__*/ _jsxs("div", {
                                        className: "pl-3 text-muted-foreground",
                                        children: [
                                            "“",
                                            r.notes,
                                            "”"
                                        ]
                                    }) : null
                                ]
                            }, r.id))
                    })
                ]
            })
        ]
    });
}
function FormShell({ title, children, error, confirmLabel, confirmDisabled, loading, onConfirm, onCancel, confirmTestId }) {
    return /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
        tray: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    className: "font-medium",
                    children: title
                }),
                children,
                error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(error, 'Could not save')
                }) : null
            ]
        }),
        onCancel: onCancel,
        onConfirm: onConfirm,
        confirmLabel: confirmLabel,
        confirmDisabled: confirmDisabled,
        loading: loading,
        cancelProps: {
            variant: 'ghost'
        },
        confirmProps: {
            'data-testid': confirmTestId
        }
    });
}
/** D98: a date field never defaults to today; it offers today as a suggestion. */ export function UseTodayButton({ onUse, testId }) {
    return /*#__PURE__*/ _jsx(SpecraButton, {
        variant: "link",
        size: "inline",
        onClick: ()=>onUse(todayISO()),
        "data-testid": testId,
        children: "Use today’s date"
    });
}
/** SAD-902: propose correcting a recorded submission's date, note or link. */ function EditSubmissionForm({ programId, occurrenceId, submission, onDone }) {
    const propose = useProposeSubmissionEdit(programId, {
        errors: 'inline',
        success: 'Edit proposed — awaiting a second person’s approval.'
    });
    const [submittedOn, setSubmittedOn] = useState(submission.submittedOn || '');
    const [submissionNote, setSubmissionNote] = useState(submission.note || '');
    const [link, setLink] = useState(submission.attachments.find((a)=>a.url)?.url ?? '');
    const [note, setNote] = useState('');
    const busy = propose.isPending;
    const ready = note.trim() !== '' && !!submittedOn;
    const submit = ()=>{
        const replace = link.trim() !== '';
        propose.mutate({
            occurrenceId,
            submissionId: submission.id,
            submittedOn,
            submissionNote: submissionNote.trim(),
            note: note.trim(),
            replaceAttachments: replace,
            attachments: replace ? [
                {
                    url: link.trim(),
                    label: submission.attachments.find((a)=>a.url)?.label || ''
                }
            ] : []
        }, {
            onSuccess: onDone
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.cmOccurrence.editDrawer,
        children: /*#__PURE__*/ _jsxs(FormShell, {
            title: "Edit submission",
            onCancel: onDone,
            onConfirm: submit,
            confirmLabel: "Propose edit",
            confirmDisabled: !ready || busy,
            loading: busy,
            confirmTestId: Testids.cmOccurrence.editSave,
            error: propose.error,
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: "A second person must approve before the filing changes. Until then the original stands, including on-time and late."
                }),
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "Submitted on",
                    required: true,
                    value: submittedOn,
                    onValueChange: setSubmittedOn,
                    "data-testid": Testids.cmOccurrence.editSubmittedOn
                }),
                /*#__PURE__*/ _jsx(UseTodayButton, {
                    onUse: setSubmittedOn
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Note on the filing",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: submissionNote,
                        onChange: (e)=>setSubmissionNote(e.target.value),
                        rows: 2,
                        "data-testid": Testids.cmOccurrence.editSubmissionNote
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Link (replaces current links when set)",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: link,
                        onChange: (e)=>setLink(e.target.value),
                        placeholder: "https://",
                        "data-testid": Testids.cmOccurrence.editLink
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why this edit",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        rows: 2,
                        "data-testid": Testids.cmOccurrence.editNote
                    })
                })
            ]
        })
    });
}
/** SAD-902: propose adding a file or link to a recorded submission. */ function AddEvidenceForm({ programId, occurrenceId, submission, onDone }) {
    const propose = useProposeSubmissionEvidence(programId, {
        errors: 'inline',
        success: 'Evidence proposed — awaiting a second person’s approval.'
    });
    const upload = useUploadArtifacts();
    const [url, setUrl] = useState('');
    const [note, setNote] = useState('');
    const [artifactId, setArtifactId] = useState('');
    const [fileName, setFileName] = useState('');
    const [uploadError, setUploadError] = useState(null);
    const busy = propose.isPending || upload.isPending;
    const ready = note.trim() !== '' && (!!url.trim() || !!artifactId);
    const submit = ()=>{
        const attachments = artifactId ? [
            {
                artifactId,
                label: fileName.trim() || 'File'
            }
        ] : [
            {
                url: url.trim(),
                label: linkLabelFromUrl(url.trim())
            }
        ];
        propose.mutate({
            occurrenceId,
            submissionId: submission.id,
            note: note.trim(),
            attachments
        }, {
            onSuccess: onDone
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.cmOccurrence.evidenceDrawer,
        children: /*#__PURE__*/ _jsxs(FormShell, {
            title: "Add evidence",
            onCancel: onDone,
            onConfirm: ()=>void submit(),
            confirmLabel: "Propose evidence",
            confirmDisabled: !ready || busy,
            loading: busy,
            confirmTestId: Testids.cmOccurrence.evidenceSave,
            error: propose.error ?? uploadError,
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: "Files or links are added only after a second person approves. The original filing stands until then."
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Link",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: url,
                        onChange: (e)=>setUrl(e.target.value),
                        placeholder: "https://",
                        "data-testid": Testids.cmOccurrence.evidenceLink
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Or upload a file",
                    children: /*#__PURE__*/ _jsx("input", {
                        type: "file",
                        "data-testid": Testids.cmOccurrence.evidenceFile,
                        onChange: async (e)=>{
                            const f = e.target.files?.[0];
                            if (!f) return;
                            setUploadError(null);
                            try {
                                const up = await upload.mutateAsync({
                                    files: [
                                        f
                                    ]
                                });
                                const art = up.files[0];
                                if (!art) {
                                    const why = up.failed[0];
                                    setUploadError(new Error(why ? `Could not upload ${why.name}: ${why.error}` : 'Upload finished with no file — try again.'));
                                    return;
                                }
                                setArtifactId(art.artifactId);
                                setFileName(art.name);
                            } catch (err) {
                                setArtifactId('');
                                setFileName('');
                                setUploadError(err);
                            }
                        }
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why add this",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        rows: 2,
                        "data-testid": Testids.cmOccurrence.evidenceNote
                    })
                })
            ]
        })
    });
}
function ProposeForm({ programId, occurrenceId, kind, eventName, dependents, onExtend, onDone }) {
    const waiver = useProposeWaiver(programId, {
        errors: 'inline'
    });
    const notOwed = useProposeNotOwed(programId, {
        errors: 'inline',
        success: 'Marked not owed, pending a second person’s approval.'
    });
    const evidence = useUploadEvidenceFiles(programId);
    const [note, setNote] = useState('');
    const [agreed, setAgreed] = useState(false);
    // The contractor's program owner decided the waiver: their reason, no COR.
    const [owner, setOwner] = useState(false);
    const [cor, setCor] = useState(EMPTY_COR_ANSWER);
    const [notOwedReason, setNotOwedReason] = useState('generated_in_error');
    const m = kind === 'waiver' ? waiver : notOwed;
    const busy = m.isPending || evidence.isPending;
    // SAD-866: a waiver is proposed with a note; the COR's fields come later,
    // or now when the COR already agreed.
    const ready = kind === 'waiver' ? agreed && !owner ? corAnswerProblem(cor) === null : note.trim() !== '' : note.trim() !== '';
    const submit = async ()=>{
        if (kind !== 'waiver') {
            notOwed.mutate({
                occurrenceId,
                reason: notOwedReason,
                note: note.trim()
            }, {
                onSuccess: onDone
            });
            return;
        }
        if (owner) {
            waiver.mutate({
                occurrenceId,
                reason: note.trim(),
                authority: PROGRAM_OWNER
            }, {
                onSuccess: (r)=>{
                    toast.success(r.decision?.step === 'approved' ? 'Waived by program owner decision.' : 'Waiver recorded as the program owner’s decision. A second person must approve it.');
                    onDone();
                }
            });
            return;
        }
        let evidenceSet = {
            documentId: '',
            attachments: []
        };
        if (agreed && cor.files.length > 0) {
            try {
                evidenceSet = await evidence.mutateAsync(cor.files);
            } catch  {
                return; // evidence.error shows inline; nothing was proposed
            }
        }
        waiver.mutate(agreed ? {
            occurrenceId,
            note: note.trim(),
            waivedOn: cor.on,
            reason: cor.reason.trim(),
            waiverDocumentId: evidenceSet.documentId,
            ...evidenceSet.attachments.length > 0 ? {
                attachments: evidenceSet.attachments
            } : {}
        } : {
            occurrenceId,
            note: note.trim()
        }, {
            onSuccess: ()=>{
                toast.success(agreed ? 'Waiver recorded as agreed by the COR. A second person must approve it.' : 'Waiver proposed. The date stays owed until the COR agrees and a second person approves.');
                onDone();
            }
        });
    };
    return /*#__PURE__*/ _jsx(FormShell, {
        title: kind === 'waiver' ? 'Propose a waiver of this date' : NOT_OWED_COPY.title,
        error: evidence.error ?? m.error,
        confirmLabel: kind === 'waiver' ? 'Propose' : 'Mark not owed',
        confirmDisabled: !ready || busy,
        loading: busy,
        onConfirm: ()=>void submit(),
        onCancel: onDone,
        confirmTestId: Testids.cmOccurrence.proposeSave,
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.cmOccurrence.proposeDrawer,
            className: "space-y-3",
            children: [
                eventName ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    "data-testid": Testids.cmOccurrence.strandWarning,
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        onClick: onExtend,
                        children: "Propose extension"
                    }),
                    children: [
                        kind === 'waiver' ? 'Waiving' : 'Marking not owed',
                        " ",
                        eventName,
                        " leaves",
                        ' ',
                        dependents === 0 ? 'the deliverables that follow it' : `${dependents} deliverable${dependents === 1 ? '' : 's'} that follow it`,
                        ' ',
                        "waiting with no firm date. If the government moved the date, propose an extension instead."
                    ]
                }) : null,
                kind === 'waiver' ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            size: "caption",
                            tone: "muted",
                            children: owner ? 'Our program owner decided this date is not required. No COR is involved: the date stays owed until a second person approves (an admin, a delivery VP or the program lead applies it at once).' : 'A waiver is the client excusing a date that was owed. Proposing changes nothing yet: the date stays owed, and late if it is late, until the COR agrees and a second person approves.'
                        }),
                        /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                            label: "Our program owner decided it, not the COR",
                            description: "Record the owner's own decision and reason. It never reads as the COR's.",
                            controlProps: {
                                checked: owner,
                                disabled: busy,
                                onCheckedChange: (v)=>setOwner(v === true),
                                ...{
                                    'data-testid': Testids.cmOccurrence.proposeOwnerDecided
                                }
                            }
                        }),
                        owner ? /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "The program owner's reason",
                            required: true,
                            hint: "e.g. “Monthly report delivered instead; weekly not required”",
                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                rows: 2,
                                value: note,
                                onChange: (e)=>setNote(e.target.value),
                                "data-testid": Testids.cmOccurrence.proposeOwnerReason
                            })
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Note",
                                    required: !agreed,
                                    hint: "Why you propose it, for the conversation with the client.",
                                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        rows: 2,
                                        value: note,
                                        onChange: (e)=>setNote(e.target.value),
                                        "data-testid": Testids.cmOccurrence.proposeNote
                                    })
                                }),
                                /*#__PURE__*/ _jsx(CorAgreedShortcut, {
                                    agreed: agreed,
                                    onAgreed: setAgreed,
                                    value: cor,
                                    onChange: setCor,
                                    disabled: busy,
                                    onTestId: Testids.cmOccurrence.proposeWaivedOn,
                                    reasonTestId: Testids.cmOccurrence.proposeReason
                                })
                            ]
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "info",
                            variant: "framed",
                            "data-testid": Testids.cmOccurrence.notOwedHelp,
                            children: NOT_OWED_COPY.help
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Why this date should not exist",
                            required: true,
                            children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                value: notOwedReason,
                                onChange: (e)=>setNotOwedReason(e.target.value),
                                "data-testid": Testids.cmOccurrence.proposeNotOwedReason,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "generated_in_error",
                                        children: NOT_OWED_COPY.reasons.generated_in_error
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "not_owed",
                                        children: NOT_OWED_COPY.reasons.not_owed
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Explain, in a sentence",
                            required: true,
                            hint: "A second person reads this before approving.",
                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                rows: 2,
                                value: note,
                                onChange: (e)=>setNote(e.target.value),
                                "data-testid": Testids.cmOccurrence.proposeReason
                            })
                        })
                    ]
                })
            ]
        })
    });
}
function ExtensionForm({ programId, occurrenceId, currentDue, dependents, onDone }) {
    const extend = useProposeExtension(programId, {
        errors: 'inline'
    });
    const evidence = useUploadEvidenceFiles(programId);
    const [dueOn, setDueOn] = useState('');
    const [note, setNote] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [cor, setCor] = useState(EMPTY_COR_ANSWER);
    const busy = evidence.isPending || extend.isPending;
    const ready = dueOn !== '' && dueOn !== currentDue && (agreed ? corAnswerProblem(cor) === null : note.trim() !== '') && !busy;
    // The evidence is uploaded and recorded first; the proposal then names it.
    const propose = async ()=>{
        let evidenceSet = {
            documentId: '',
            attachments: []
        };
        if (agreed && cor.files.length > 0) {
            try {
                evidenceSet = await evidence.mutateAsync(cor.files);
            } catch  {
                return; // evidence.error shows inline; nothing was proposed
            }
        }
        extend.mutate({
            occurrenceId,
            dueOn,
            note: note.trim(),
            ...agreed ? {
                reason: cor.reason.trim(),
                corAgreedOn: cor.on
            } : {},
            ...evidenceSet.documentId ? {
                evidenceDocumentId: evidenceSet.documentId
            } : {},
            ...evidenceSet.attachments.length > 0 ? {
                attachments: evidenceSet.attachments
            } : {}
        }, {
            onSuccess: ()=>{
                toast.success(agreed ? 'Extension recorded as agreed by the COR. A second person must approve it.' : 'Extension proposed. The date is unchanged until the COR agrees and a second person approves.');
                onDone();
            }
        });
    };
    return /*#__PURE__*/ _jsx(FormShell, {
        title: "Propose an extension of this date",
        error: evidence.error ?? extend.error,
        confirmLabel: "Propose",
        confirmDisabled: !ready,
        loading: busy,
        onConfirm: ()=>void propose(),
        onCancel: onDone,
        confirmTestId: Testids.cmOccurrence.extensionSave,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            "data-testid": Testids.cmOccurrence.extensionDrawer,
            children: [
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "New due date",
                    required: true,
                    value: dueOn,
                    onValueChange: setDueOn,
                    hint: currentDue ? `Currently ${cmDate(currentDue)}` : 'No due date stated today',
                    "data-testid": Testids.cmOccurrence.extensionDueOn
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Note",
                    required: !agreed,
                    hint: `The date stays as it is until the COR agrees and a second person approves. ${dependents > 0 ? `Then the ${dependents} deliverable${dependents === 1 ? '' : 's'} that follow this date move with it.` : 'Then the deliverables that follow this date move with it.'}`,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        placeholder: "e.g. Ask the COR for two more weeks: the data arrives late",
                        "data-testid": Testids.cmOccurrence.extensionNote
                    })
                }),
                /*#__PURE__*/ _jsx(CorAgreedShortcut, {
                    agreed: agreed,
                    onAgreed: setAgreed,
                    value: cor,
                    onChange: setCor,
                    disabled: busy,
                    onTestId: Testids.cmOccurrence.extensionCorOn,
                    reasonTestId: Testids.cmOccurrence.extensionReason
                })
            ]
        })
    });
}
/** SAD-866: the COR's answer on the date's proposal, agreed or declined. */ function CorForm({ programId, decision, onDone }) {
    const [value, setValue] = useState(EMPTY_COR_ANSWER);
    const [failed, setFailed] = useState(null);
    const runner = useCorAnswerRunner(programId);
    const problem = corAnswerProblem(value);
    const save = async ()=>{
        setFailed(null);
        const { failures } = await runner.run([
            {
                changeSetId: decision.changeSetId,
                name: 'This date'
            }
        ], value);
        if (failures.length > 0) {
            setFailed(failures[0]);
            return;
        }
        toast.success(value.answer === 'agreed' ? 'Recorded the COR’s agreement. A second person must approve it.' : 'Recorded the COR declining. The date stays owed.');
        onDone();
    };
    return /*#__PURE__*/ _jsx(FormShell, {
        title: "Record the COR’s answer",
        error: failed ? new Error(failed) : undefined,
        confirmLabel: "Record",
        confirmDisabled: !!problem || runner.busy,
        loading: runner.busy,
        onConfirm: ()=>void save(),
        onCancel: onDone,
        confirmTestId: Testids.cmOccurrence.corSave,
        children: /*#__PURE__*/ _jsx("div", {
            className: "space-y-3",
            "data-testid": Testids.cmOccurrence.corForm,
            children: /*#__PURE__*/ _jsx(CorAnswerFields, {
                value: value,
                onChange: setValue,
                disabled: runner.busy,
                testIds: {
                    answer: Testids.cmOccurrence.corAnswer,
                    on: Testids.cmOccurrence.corOn,
                    reason: Testids.cmOccurrence.corReason,
                    evidence: Testids.cmOccurrence.corEvidence
                }
            })
        })
    });
}
function MoveForm({ programId, occurrenceId, currentDue, onDone }) {
    const move = useMoveOccurrence(programId, {
        errors: 'inline',
        success: 'Due date moved'
    });
    const [dueOn, setDueOn] = useState('');
    const [reason, setReason] = useState('');
    const ready = dueOn !== '' && dueOn !== currentDue && reason.trim() !== '';
    return /*#__PURE__*/ _jsx(FormShell, {
        title: "Move the due date",
        error: move.error,
        confirmLabel: "Move",
        confirmDisabled: !ready,
        loading: move.isPending,
        onConfirm: ()=>move.mutate({
                occurrenceId,
                dueOn,
                reason: reason.trim()
            }, {
                onSuccess: onDone
            }),
        onCancel: onDone,
        confirmTestId: Testids.cmOccurrence.moveSave,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            "data-testid": Testids.cmOccurrence.moveDrawer,
            children: [
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "New due date",
                    required: true,
                    value: dueOn,
                    onValueChange: setDueOn,
                    hint: currentDue ? `Currently ${cmDate(currentDue)}` : 'No due date stated today',
                    "data-testid": Testids.cmOccurrence.moveDueOn
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why it moved",
                    required: true,
                    hint: "e.g. “COR email of 3 Mar extended it”",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: reason,
                        onChange: (e)=>setReason(e.target.value),
                        "data-testid": Testids.cmOccurrence.moveReason
                    })
                })
            ]
        })
    });
}
/** Remove the date's waiver: it is owed again as before (REVOKE_HELP). */ function RevokeWaiverForm({ programId, occurrenceId, onDone }) {
    const revoke = useProposeWaiverRevoke(programId, {
        errors: 'inline',
        success: (r)=>revokeOutcome(r.decision?.step)
    });
    const [reason, setReason] = useState('');
    return /*#__PURE__*/ _jsx(FormShell, {
        title: "Remove the waiver",
        error: revoke.error,
        confirmLabel: "Remove waiver",
        confirmDisabled: reason.trim() === '',
        loading: revoke.isPending,
        onConfirm: ()=>revoke.mutate({
                occurrenceId,
                reason: reason.trim()
            }, {
                onSuccess: onDone
            }),
        onCancel: onDone,
        confirmTestId: Testids.cmOccurrence.revokeWaiverSave,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            "data-testid": Testids.cmOccurrence.revokeWaiverForm,
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: REVOKE_HELP
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why remove it",
                    required: true,
                    hint: "Kept with the waiver it removes.",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: reason,
                        onChange: (e)=>setReason(e.target.value),
                        "data-testid": Testids.cmOccurrence.revokeWaiverReason
                    })
                })
            ]
        })
    });
}
function EndDecisionForm({ programId, decision, withdraw, onDone }) {
    const reject = useRejectDecision(programId, {
        errors: 'inline',
        success: withdraw ? 'Proposal withdrawn' : 'Proposal rejected'
    });
    const [reason, setReason] = useState('');
    return /*#__PURE__*/ _jsx(FormShell, {
        title: withdraw ? 'Withdraw your proposal' : 'Reject this proposal',
        error: reject.error,
        confirmLabel: withdraw ? 'Withdraw' : 'Reject',
        confirmDisabled: reason.trim() === '',
        loading: reject.isPending,
        onConfirm: ()=>reject.mutate({
                changeSetId: decision.changeSetId,
                reason: reason.trim()
            }, {
                onSuccess: onDone
            }),
        onCancel: onDone,
        confirmTestId: Testids.cmOccurrence.decisionConfirm,
        children: /*#__PURE__*/ _jsx(SpecraFormField, {
            label: "Reason",
            required: true,
            hint: withdraw ? 'Kept in the history. The date is unchanged.' : 'The proposer sees this. The date is unchanged and can be proposed again.',
            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                rows: 2,
                value: reason,
                onChange: (e)=>setReason(e.target.value),
                "data-testid": Testids.cmOccurrence.decisionReason
            })
        })
    });
}
export { NOT_OWED_REASON_LABEL };
